import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { useSeo } from "@/seo";
import { dismiss, dismissed } from "@/motion";
import Hero from "@/components/home/Hero";
import IntroVideo from "@/components/home/IntroVideo";
import WatchTable from "@/components/home/WatchTable";
import { Feed, Leagues, Sets, Stats, Upcoming } from "@/components/home/Sidebar";
import { Collection, NewMilestones, Updated, Upsell, VisitDelta } from "@/components/home/Personal";

// Trois états : visiteur, gratuit, premium. Un utilisateur premium ne voit AUCUNE mention du premium.
function pickUpsell(d, t) {
  if (d.state !== "free") return null;
  const atList = d.list && d.list.limit !== null && d.list.used >= d.list.limit;
  const c = [
    d.collection?.free_set_limit_reached && { id: "a", slot: "collection", text: t("home.upsell_sets") },
    d.delta?.players_moved > 0 && { id: "b", slot: "delta", text: t("home.upsell_alerts") },
    atList && { id: "c", slot: "table", text: t("home.upsell_list") },
  ].filter(Boolean);
  return c.find((u) => !dismissed(u.id)) || null;
}

export default function Home() {
  const { t, lang } = useI18n();
  const { me } = useAuth();
  const [d, setD] = useState(null);
  const [, bump] = useState(0);
  const [hookOff, setHookOff] = useState(() => {
    try { return localStorage.getItem("rr_hook_dismissed") === "1"; } catch (e) { return false; }
  });
  const dismissHook = () => {
    try { localStorage.setItem("rr_hook_dismissed", "1"); } catch (e) { /* no-op */ }
    setHookOff(true);
  };
  useSeo(t("seo.home_title"), t("seo.home_desc"), "/");

  const load = useCallback(() => api.get("/home").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, me?.email]);

  if (!d) return <div className="pt-6 wl-mono text-[10px] uppercase tracking-[.04em] text-slate-500" data-testid="home-loading">{t("common.loading")}</div>;

  const up = pickUpsell(d, t);
  const upsellFor = (slot) => (up && up.slot === slot ? <Upsell id={up.id} text={up.text} t={t} onDismiss={() => { dismiss(up.id); bump((n) => n + 1); }} /> : null);

  return (
    <div className="wl-scope -mx-4 -mb-16 px-4 pb-12 pt-2 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8" data-testid="home-page" data-state={d.state}>
      <NewMilestones since={d.server_time} onRefresh={load} t={t} />
      {/* Accroche éditoriale : visiteurs uniquement. Le « × » (dans la vignette) ferme le bandeau ENTIER,
          choix mémorisé ; une fois rejeté ou connecté, rien n'est rendu et le tableau remonte. */}
      {d.state === "visitor" && !hookOff && (
        <div className="grid gap-x-6 gap-y-3 pt-1 pb-3 xl:grid-cols-3" data-testid="home-tagline">
          <div className="min-w-0 xl:col-span-2">
            <h2 className="font-sg text-[30px] font-bold uppercase leading-[0.95] tracking-[-0.02em] text-[var(--wl-text)] sm:text-[40px]" data-testid="home-hook-title">{t("home.hook_title")}</h2>
            <p className="mt-2 max-w-[640px] text-[15px] leading-snug text-[var(--wl-muted)]" data-testid="home-hook-line">{t("home.hook_line")}</p>
            <Link to="/auth" data-testid="home-cta-signup" className="mt-3 inline-flex items-center rounded-md bg-[var(--wl-accent)] px-4 py-2 font-sg text-[13px] font-bold uppercase tracking-[.04em] text-black transition-transform hover:scale-[1.03]">{t("home.hook_cta")}</Link>
          </div>
          <div className="min-w-0 xl:col-span-1">
            <IntroVideo lang={lang} t={t} onDismiss={dismissHook} />
          </div>
        </div>
      )}
      <div className="flex justify-end py-1"><Updated at={d.updated_at} t={t} lang={lang} /></div>
      {/* Grille 2/3 – 1/3 à partir de 1280 px ; une seule colonne en dessous, la watchlist reste en position 2. */}
      <div className="grid gap-x-6 gap-y-4 xl:grid-cols-3" data-testid="home-grid">
        <div className="min-w-0 space-y-3 xl:col-span-2" data-testid="home-main">
          <Hero hero={d.hero} t={t} lang={lang} />
          <VisitDelta delta={d.delta} upsell={upsellFor("delta")} t={t} lang={lang} />
          <WatchTable rows={d.table} leagues={d.leagues} total={d.stats.players} t={t} lang={lang} upsell={upsellFor("table")} />
        </div>
        <aside className="min-w-0 space-y-4" data-testid="home-side">
          <Stats stats={d.stats} fact={d.fact} brk={d.break_info} lang={lang} t={t} />
          <Feed feed={d.feed} nextFixture={d.next_fixture} brk={d.break_info} t={t} lang={lang} />
          <Sets sets={d.sets} t={t} />
          <Upcoming fixtures={d.upcoming} nextFixture={d.next_fixture} brk={d.break_info} t={t} lang={lang} />
          <Leagues leagues={d.leagues_covered} t={t} />
        </aside>
      </div>
      <div className="mt-4" data-testid="home-bottom">
        <Collection state={d.state} collection={d.collection} upsell={upsellFor("collection")} t={t} />
      </div>
    </div>
  );
}
